import axios from 'axios'
import queryString from 'query-string'
import React from 'react'
import { matchPath } from 'react-router'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { ExtendAnalyticsBrowser } from 'src/analytics/AnalyticsContext'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  ApproveExpensesToastMessage,
  DeleteExpenseReportsToastMessage,
  DeleteExpensesToastMessage,
  MarkExpenseReportsAsPaidToastMessage,
  RejectExpensesToastMessage,
  SubmitExpenseReportToastMessage,
  UpdateExpenseReportNameSuccessToastMessage,
  UpdateExpenseToastMessage,
} from 'src/app/Toast/Toast'
import { ExpenseFields } from 'src/components/ExpenseForm/ExpenseForm'
import {
  ExpenseReportResponse,
  ExpenseResponse,
} from 'src/models/expenseReport.model'
import { UserListResponse } from 'src/models/user.models'
import { closeModal } from 'src/navigation/Lnk'
import { expenseReportUrl } from 'src/navigation/urls'
import { ManagerApprovalReimbursementResponse } from 'src/queries/expenseReview.queries'
import {
  createSetIsDeleteExpensePopupOpen,
  createSetIsDeleteExpenseReportsPopupOpen,
  createSetIsMarkExpenseReportsPaidPopupOpen,
  createSetIsSubmitExpenseReportPopupOpen,
} from 'src/redux/actions/app.actions'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { toDollars } from 'src/utils/currency.utils'
import { BackendErrorPayload } from 'src/utils/error.utils'

// for react-query implementation of manager approval flow
export const PATCH_REIMBURSEMENT_MANAGER_APPROVAL =
  'data/expenses/PATCH_REIMBURSEMENT_MANAGER_APPROVAL'
export interface PatchReimbursementManagerApprovalAction {
  type: typeof PATCH_REIMBURSEMENT_MANAGER_APPROVAL
  payload: ManagerApprovalReimbursementResponse[]
}
export type PatchReimbursementManagerApprovalActions =
  PatchReimbursementManagerApprovalAction

export const POST_CREATE_EXPENSE_REPORT_INIT =
  'data/expenses/POST_CREATE_EXPENSE_REPORT_INIT'
export const POST_CREATE_EXPENSE_REPORT_SUCCESS =
  'data/expenses/POST_CREATE_EXPENSE_REPORT_SUCCESS'
export const POST_CREATE_EXPENSE_REPORT_FAILURE =
  'data/expenses/POST_CREATE_EXPENSE_REPORT_FAILURE'

export type PostCreateExpenseReportSuccessPayload = ExpenseReportResponse

export type PostCreateExpenseReportErrorPayload = BackendErrorPayload<string>

export interface PostCreateExpenseReportInitAction {
  type: typeof POST_CREATE_EXPENSE_REPORT_INIT
}
export interface PostCreateExpenseReportSuccessAction {
  type: typeof POST_CREATE_EXPENSE_REPORT_SUCCESS
  payload: PostCreateExpenseReportSuccessPayload
}
export interface PostCreateExpenseReportFailureAction {
  type: typeof POST_CREATE_EXPENSE_REPORT_FAILURE
  payload: PostCreateExpenseReportErrorPayload
}

export type PostCreateExpenseReportActions =
  | PostCreateExpenseReportInitAction
  | PostCreateExpenseReportSuccessAction
  | PostCreateExpenseReportFailureAction

const createPostCreateExpenseReportInitAction =
  (): PostCreateExpenseReportInitAction => ({
    type: POST_CREATE_EXPENSE_REPORT_INIT,
  })
const createPostCreateExpenseReportSuccessAction = (
  payload: PostCreateExpenseReportSuccessPayload,
): PostCreateExpenseReportSuccessAction => ({
  type: POST_CREATE_EXPENSE_REPORT_SUCCESS,
  payload,
})
const createPostCreateExpenseReportFailureAction = (
  payload: PostCreateExpenseReportErrorPayload,
): PostCreateExpenseReportFailureAction => ({
  type: POST_CREATE_EXPENSE_REPORT_FAILURE,
  payload,
})

export const postCreateExpenseReport = (
  expenseName: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostCreateExpenseReportActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostCreateExpenseReportActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostCreateExpenseReportInitAction())
    return axios
      .post<PostCreateExpenseReportSuccessPayload>(
        `/expensereports`,
        {
          name: expenseName,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostCreateExpenseReportSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createPostCreateExpenseReportFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_CREATE_EXPENSE_INIT = 'data/expenses/POST_CREATE_EXPENSE_INIT'
export const POST_CREATE_EXPENSE_SUCCESS =
  'data/expenses/POST_CREATE_EXPENSE_SUCCESS'
export const POST_CREATE_EXPENSE_FAILURE =
  'data/expenses/POST_CREATE_EXPENSE_FAILURE'

export type PostCreateExpenseSuccessPayload = ExpenseResponse

export type PostCreateExpenseErrorPayload = BackendErrorPayload<string>

export interface PostCreateExpenseInitAction {
  type: typeof POST_CREATE_EXPENSE_INIT
}
export interface PostCreateExpenseSuccessAction {
  type: typeof POST_CREATE_EXPENSE_SUCCESS
  payload: PostCreateExpenseSuccessPayload
}
export interface PostCreateExpenseFailureAction {
  type: typeof POST_CREATE_EXPENSE_FAILURE
  payload: PostCreateExpenseErrorPayload
}

export type PostCreateExpenseActions =
  | PostCreateExpenseInitAction
  | PostCreateExpenseSuccessAction
  | PostCreateExpenseFailureAction

const createPostCreateExpenseInitAction = (): PostCreateExpenseInitAction => ({
  type: POST_CREATE_EXPENSE_INIT,
})
const createPostCreateExpenseSuccessAction = (
  payload: PostCreateExpenseSuccessPayload,
): PostCreateExpenseSuccessAction => ({
  type: POST_CREATE_EXPENSE_SUCCESS,
  payload,
})
const createPostCreateExpenseFailureAction = (
  payload: PostCreateExpenseErrorPayload,
): PostCreateExpenseFailureAction => ({
  type: POST_CREATE_EXPENSE_FAILURE,
  payload,
})

export const postCreateExpense = (
  body: ExpenseFields,
  isExpenseReportPage: boolean,
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<Promise<void>, RootState, void, PostCreateExpenseActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostCreateExpenseActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostCreateExpenseInitAction())
    return axios
      .post<PostCreateExpenseSuccessPayload>(
        `/expenses`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostCreateExpenseSuccessAction(response.data))
          if (body.expenseReportId)
            dispatch(getExpenseReport(body.expenseReportId))
          isExpenseReportPage &&
            analytics?.track(
              'out of pocket expense report details page - add expense - button press',
              {
                interaction: 'button press',
                intent: 'add expense',
                Expense_Name: body.expenseDate,
                Amount_Spent: toDollars(body.amountCents ?? 0),
                Expense_Date: body.expenseDate,
                Receipt_Uploaded: body.receiptAttachmentIds
                  ? body.receiptAttachmentIds.length > 0
                  : false,
              },
            )
        },
        (err) => {
          dispatch(createPostCreateExpenseFailureAction(err.response.data))
        },
      )
  }
}

export const CLEAR_EXPENSE_REPORT = 'app/expensereport/CLEAR_EXPENSE_REPORT'

export const GET_EXPENSE_REPORT_INIT = 'data/expenses/GET_EXPENSE_REPORT_INIT'
export const GET_EXPENSE_REPORT_SUCCESS =
  'data/expenses/GET_EXPENSE_REPORT_SUCCESS'
export const GET_EXPENSE_REPORT_FAILURE =
  'data/expenses/GET_EXPENSE_REPORT_FAILURE'

export type GetExpenseReportSuccessPayload = ExpenseReportResponse

export type GetExpenseReportErrorPayload = BackendErrorPayload<string>

export interface GetExpenseReportInitAction {
  type: typeof GET_EXPENSE_REPORT_INIT
}
export interface GetExpenseReportSuccessAction {
  type: typeof GET_EXPENSE_REPORT_SUCCESS
  payload: GetExpenseReportSuccessPayload
}
export interface GetExpenseReportFailureAction {
  type: typeof GET_EXPENSE_REPORT_FAILURE
  payload: GetExpenseReportErrorPayload
}

export interface ClearExpenseReportAction {
  type: typeof CLEAR_EXPENSE_REPORT
}

export type GetExpenseReportActions =
  | GetExpenseReportInitAction
  | GetExpenseReportSuccessAction
  | GetExpenseReportFailureAction
  | ClearExpenseReportAction

const createGetExpenseReportInitAction = (): GetExpenseReportInitAction => ({
  type: GET_EXPENSE_REPORT_INIT,
})
const createGetExpenseReportSuccessAction = (
  payload: GetExpenseReportSuccessPayload,
): GetExpenseReportSuccessAction => ({
  type: GET_EXPENSE_REPORT_SUCCESS,
  payload,
})

const createGetExpenseReportFailureAction = (
  payload: GetExpenseReportErrorPayload,
): GetExpenseReportFailureAction => ({
  type: GET_EXPENSE_REPORT_FAILURE,
  payload,
})

export const getExpenseReport = (
  id: string,
): ThunkAction<Promise<void>, RootState, void, GetExpenseReportActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetExpenseReportActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetExpenseReportInitAction())
    return axios
      .get<GetExpenseReportSuccessPayload>(
        `/expensereports/${id}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetExpenseReportSuccessAction(response.data))
        },
        async (err) => {
          dispatch(createGetExpenseReportFailureAction(err.response.data))
        },
      )
  }
}

export const GET_SUBMITTER_EXPENSE_REPORTS_INIT =
  'data/expenses/GET_SUBMITTER_EXPENSE_REPORTS_INIT '
export const GET_SUBMITTER_EXPENSE_REPORTS_SUCCESS =
  'data/expenses/GET_SUBMITTER_EXPENSE_REPORTS_SUCCESS'
export const GET_SUBMITTER_EXPENSE_REPORTS_FAILURE =
  'data/expenses/GET_SUBMITTER_EXPENSE_REPORTS_FAILURE'

export interface GetSubmitterExpenseReportsSuccessPayload {
  expenseReports: ExpenseReportResponse[]
  pagination: {
    numberOfPages: number
    page: number
    pageItemCount: number
    totalItems: number
  }
}
export type GetSubmitterExpenseReportsErrorPayload = BackendErrorPayload<string>
export interface GetSubmitterExpenseReportsInitAction {
  type: typeof GET_SUBMITTER_EXPENSE_REPORTS_INIT
}
export interface GetSubmitterExpenseReportsSuccessAction {
  type: typeof GET_SUBMITTER_EXPENSE_REPORTS_SUCCESS
  payload: GetSubmitterExpenseReportsSuccessPayload
}
export interface GetSubmitterExpenseReportsFailureAction {
  type: typeof GET_SUBMITTER_EXPENSE_REPORTS_FAILURE
  payload: unknown
}

export type GetSubmitterExpenseReportsActions =
  | GetSubmitterExpenseReportsInitAction
  | GetSubmitterExpenseReportsSuccessAction
  | GetSubmitterExpenseReportsFailureAction

const createGetSubmitterExpenseReportsInitAction =
  (): GetSubmitterExpenseReportsInitAction => ({
    type: GET_SUBMITTER_EXPENSE_REPORTS_INIT,
  })
const createGetSubmitterExpenseReportsSuccessAction = (
  payload: GetSubmitterExpenseReportsSuccessPayload,
): GetSubmitterExpenseReportsSuccessAction => ({
  type: GET_SUBMITTER_EXPENSE_REPORTS_SUCCESS,
  payload,
})
const createGetSubmitterExpenseReportsFailureAction = (
  response: unknown,
): GetSubmitterExpenseReportsFailureAction => ({
  type: GET_SUBMITTER_EXPENSE_REPORTS_FAILURE,
  payload: response,
})

export interface GetSubmitterExpenseReportsQueryParams {
  status?: string
  sortDirection?: string
  page?: number
}

export const getSubmitterExpenseReports = (
  queryParams?: GetSubmitterExpenseReportsQueryParams,
): ThunkAction<void, RootState, void, GetSubmitterExpenseReportsActions> => {
  return (
    dispatch: ThunkDispatch<RootState, void, GetSubmitterExpenseReportsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetSubmitterExpenseReportsInitAction())
    return axios
      .get<GetSubmitterExpenseReportsSuccessPayload>(
        `/expensereports/submitter?${queryParams ? queryString.stringify(queryParams) : ''}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createGetSubmitterExpenseReportsSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createGetSubmitterExpenseReportsFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_SUBMITTER_EXPENSE_REPORTS_LENGTH_SUCCESS =
  'data/expenses/GET_SUBMITTER_EXPENSE_REPORTS_LENGTH_SUCCESS'

export interface GetSubmitterExpenseReportsLengthSuccessPayload {
  expenseReports: ExpenseReportResponse[]
  pagination: {
    numberOfPages: number
    page: number
    pageItemCount: number
    totalItems: number
  }
}
export interface GetSubmitterExpenseReportsLengthSuccessAction {
  type: typeof GET_SUBMITTER_EXPENSE_REPORTS_LENGTH_SUCCESS
  payload: GetSubmitterExpenseReportsLengthSuccessPayload
}

export type GetSubmitterExpenseReportsLengthActions =
  GetSubmitterExpenseReportsLengthSuccessAction

const createGetSubmitterExpenseReportsLengthSuccessAction = (
  payload: GetSubmitterExpenseReportsLengthSuccessPayload,
): GetSubmitterExpenseReportsLengthSuccessAction => ({
  type: GET_SUBMITTER_EXPENSE_REPORTS_LENGTH_SUCCESS,
  payload,
})

export const getSubmitterExpenseReportsLength = (): ThunkAction<
  void,
  RootState,
  void,
  GetSubmitterExpenseReportsLengthActions
> => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetSubmitterExpenseReportsLengthActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    return axios
      .get<GetSubmitterExpenseReportsLengthSuccessPayload>(
        `/expensereports/submitter`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then((response) => {
        dispatch(
          createGetSubmitterExpenseReportsLengthSuccessAction(response.data),
        )
      })
  }
}

export const GET_APPROVER_EXPENSE_REPORTS_INIT =
  'data/expenses/GET_APPROVER_EXPENSE_REPORTS_INIT '
export const GET_APPROVER_EXPENSE_REPORTS_SUCCESS =
  'data/expenses/GET_APPROVER_EXPENSE_REPORTS_SUCCESS'
export const GET_APPROVER_EXPENSE_REPORTS_FAILURE =
  'data/expenses/GET_APPROVER_EXPENSE_REPORTS_FAILURE'

export interface GetApproverExpenseReportsSuccessPayload {
  expenseReports: ExpenseReportResponse[]
  pagination: {
    numberOfPages: number
    page: number
    pageItemCount: number
    totalItems: number
  }
}
export type GetApproverExpenseReportsErrorPayload = BackendErrorPayload<string>
export interface GetApproverExpenseReportsInitAction {
  type: typeof GET_APPROVER_EXPENSE_REPORTS_INIT
}
export interface GetApproverExpenseReportsSuccessAction {
  type: typeof GET_APPROVER_EXPENSE_REPORTS_SUCCESS
  payload: GetApproverExpenseReportsSuccessPayload
}
export interface GetApproverExpenseReportsFailureAction {
  type: typeof GET_APPROVER_EXPENSE_REPORTS_FAILURE
  payload: unknown
}

export type GetApproverExpenseReportsActions =
  | GetApproverExpenseReportsInitAction
  | GetApproverExpenseReportsSuccessAction
  | GetApproverExpenseReportsFailureAction

const createGetApproverExpenseReportsInitAction =
  (): GetApproverExpenseReportsInitAction => ({
    type: GET_APPROVER_EXPENSE_REPORTS_INIT,
  })
const createGetApproverExpenseReportsSuccessAction = (
  payload: GetApproverExpenseReportsSuccessPayload,
): GetApproverExpenseReportsSuccessAction => ({
  type: GET_APPROVER_EXPENSE_REPORTS_SUCCESS,
  payload,
})
const createGetApproverExpenseReportsFailureAction = (
  response: unknown,
): GetApproverExpenseReportsFailureAction => ({
  type: GET_APPROVER_EXPENSE_REPORTS_FAILURE,
  payload: response,
})

export interface GetApproverExpenseReportsQueryParams {
  status?: string
  page?: number
  userId?: string
}

export const getApproverExpenseReports = (
  queryParams?: GetApproverExpenseReportsQueryParams,
): ThunkAction<void, RootState, void, GetApproverExpenseReportsActions> => {
  return (
    dispatch: ThunkDispatch<RootState, void, GetApproverExpenseReportsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetApproverExpenseReportsInitAction())
    return axios
      .get<GetApproverExpenseReportsSuccessPayload>(
        `/expensereports/approver?${queryParams ? queryString.stringify(queryParams) : ''}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createGetApproverExpenseReportsSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createGetApproverExpenseReportsFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_APPROVER_EXPENSE_REPORTS_LENGTH_SUCCESS =
  'data/expenses/GET_APPROVER_EXPENSE_REPORTS_LENGTH_SUCCESS'

export interface GetApproverExpenseReportsLengthSuccessPayload {
  expenseReports: ExpenseReportResponse[]
  pagination: {
    numberOfPages: number
    page: number
    pageItemCount: number
    totalItems: number
  }
}
export interface GetApproverExpenseReportsLengthSuccessAction {
  type: typeof GET_APPROVER_EXPENSE_REPORTS_LENGTH_SUCCESS
  payload: GetApproverExpenseReportsLengthSuccessPayload
}

export type GetApproverExpenseReportsLengthActions =
  GetApproverExpenseReportsLengthSuccessAction

const createGetApproverExpenseReportsLengthSuccessAction = (
  payload: GetApproverExpenseReportsLengthSuccessPayload,
): GetApproverExpenseReportsLengthSuccessAction => ({
  type: GET_APPROVER_EXPENSE_REPORTS_LENGTH_SUCCESS,
  payload,
})

export const getApproverExpenseReportsLength = (): ThunkAction<
  void,
  RootState,
  void,
  GetApproverExpenseReportsLengthActions
> => {
  return (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetApproverExpenseReportsLengthActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    return axios
      .get<GetApproverExpenseReportsLengthSuccessPayload>(
        `/expensereports/approver`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then((response) => {
        dispatch(
          createGetApproverExpenseReportsLengthSuccessAction(response.data),
        )
      })
  }
}

export const POST_APPROVE_EXPENSES_INIT =
  'data/expenses/POST_APPROVE_EXPENSES_INIT'
export const POST_APPROVE_EXPENSES_SUCCESS =
  'data/expenses/POST_APPROVE_EXPENSES_SUCCESS'
export const POST_APPROVE_EXPENSES_FAILURE =
  'data/expenses/POST_APPROVE_EXPENSES_FAILURE'

export type PostApproveExpensesSuccessPayload = ExpenseReportResponse

export type PostApproveExpensesErrorPayload = BackendErrorPayload<string>

export interface PostApproveExpensesInitAction {
  type: typeof POST_APPROVE_EXPENSES_INIT
}
export interface PostApproveExpensesSuccessAction {
  type: typeof POST_APPROVE_EXPENSES_SUCCESS
  payload: PostApproveExpensesSuccessPayload
}
export interface PostApproveExpensesFailureAction {
  type: typeof POST_APPROVE_EXPENSES_FAILURE
  payload: PostApproveExpensesErrorPayload
}

export type PostApproveExpensesActions =
  | PostApproveExpensesInitAction
  | PostApproveExpensesSuccessAction
  | PostApproveExpensesFailureAction

const createPostApproveExpensesInitAction =
  (): PostApproveExpensesInitAction => ({
    type: POST_APPROVE_EXPENSES_INIT,
  })
const createPostApproveExpensesSuccessAction = (
  payload: PostApproveExpensesSuccessPayload,
): PostApproveExpensesSuccessAction => ({
  type: POST_APPROVE_EXPENSES_SUCCESS,
  payload,
})
const createPostApproveExpensesFailureAction = (
  payload: PostApproveExpensesErrorPayload,
): PostApproveExpensesFailureAction => ({
  type: POST_APPROVE_EXPENSES_FAILURE,
  payload,
})

export const postApproveExpenses = (
  expenseIds: string[],
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<Promise<void>, RootState, void, PostApproveExpensesActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostApproveExpensesActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostApproveExpensesInitAction())
    return axios
      .post<PostApproveExpensesSuccessPayload>(
        `/expenses/approve`,
        {
          expenses: expenseIds,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostApproveExpensesSuccessAction(response.data))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <ApproveExpensesToastMessage
                  expensesCount={expenseIds.length}
                />
              ),
            }),
          )
          analytics?.track(
            'Expense Report Details Page - Bulk Action Approved - button press',
            {
              interaction: 'button press',
              intent: 'Bulk Action Approved',
              Total_expenses_approved: expenseIds.length,
            },
          )
        },
        (err) => {
          dispatch(createPostApproveExpensesFailureAction(err.response.data))
        },
      )
  }
}

export const POST_REJECT_EXPENSES_INIT =
  'data/expenses/POST_REJECT_EXPENSES_INIT'
export const POST_REJECT_EXPENSES_SUCCESS =
  'data/expenses/POST_REJECT_EXPENSES_SUCCESS'
export const POST_REJECT_EXPENSES_FAILURE =
  'data/expenses/POST_REJECT_EXPENSES_FAILURE'

export type PostRejectExpensesSuccessPayload = ExpenseReportResponse

export type PostRejectExpensesErrorPayload = BackendErrorPayload<string>

export interface PostRejectExpensesInitAction {
  type: typeof POST_REJECT_EXPENSES_INIT
}
export interface PostRejectExpensesSuccessAction {
  type: typeof POST_REJECT_EXPENSES_SUCCESS
  payload: PostRejectExpensesSuccessPayload
}
export interface PostRejectExpensesFailureAction {
  type: typeof POST_REJECT_EXPENSES_FAILURE
  payload: PostRejectExpensesErrorPayload
}

export type PostRejectExpensesActions =
  | PostRejectExpensesInitAction
  | PostRejectExpensesSuccessAction
  | PostRejectExpensesFailureAction

const createPostRejectExpensesInitAction =
  (): PostRejectExpensesInitAction => ({
    type: POST_REJECT_EXPENSES_INIT,
  })
const createPostRejectExpensesSuccessAction = (
  payload: PostRejectExpensesSuccessPayload,
): PostRejectExpensesSuccessAction => ({
  type: POST_REJECT_EXPENSES_SUCCESS,
  payload,
})
const createPostRejectExpensesFailureAction = (
  payload: PostRejectExpensesErrorPayload,
): PostRejectExpensesFailureAction => ({
  type: POST_REJECT_EXPENSES_FAILURE,
  payload,
})

export const postRejectExpenses = (
  expenseIds: string[],
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<Promise<void>, RootState, void, PostRejectExpensesActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostRejectExpensesActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostRejectExpensesInitAction())
    return axios
      .post<PostRejectExpensesSuccessPayload>(
        `/expenses/reject`,
        {
          expenses: expenseIds,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostRejectExpensesSuccessAction(response.data))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <RejectExpensesToastMessage expensesCount={expenseIds.length} />
              ),
            }),
          )
          analytics?.track(
            'Expense Report Details Page - Bulk Action Rejected - button press',
            {
              interaction: 'button press',
              intent: 'Bulk Action Rejected',
              Total_expenses_rejected: expenseIds.length,
            },
          )
        },
        (err) => {
          dispatch(createPostRejectExpensesFailureAction(err.response.data))
        },
      )
  }
}

export const POST_SUBMIT_EXPENSE_REPORT_INIT =
  'data/expenses/POST_SUBMIT_EXPENSE_REPORT_INIT'
export const POST_SUBMIT_EXPENSE_REPORT_SUCCESS =
  'data/expenses/POST_SUBMIT_EXPENSE_REPORT_SUCCESS'
export const POST_SUBMIT_EXPENSE_REPORT_FAILURE =
  'data/expenses/POST_SUBMIT_EXPENSE_REPORT_FAILURE'

export type PostSubmitExpenseReportSuccessPayload = ExpenseReportResponse

export type PostSubmitExpenseReportErrorPayload = BackendErrorPayload<string>

export interface PostSubmitExpenseReportInitAction {
  type: typeof POST_SUBMIT_EXPENSE_REPORT_INIT
}
export interface PostSubmitExpenseReportSuccessAction {
  type: typeof POST_SUBMIT_EXPENSE_REPORT_SUCCESS
  payload: PostSubmitExpenseReportSuccessPayload
}
export interface PostSubmitExpenseReportFailureAction {
  type: typeof POST_SUBMIT_EXPENSE_REPORT_FAILURE
  payload: PostSubmitExpenseReportErrorPayload
}

export type PostSubmitExpenseReportActions =
  | PostSubmitExpenseReportInitAction
  | PostSubmitExpenseReportSuccessAction
  | PostSubmitExpenseReportFailureAction

const createPostSubmitExpenseReportInitAction =
  (): PostSubmitExpenseReportInitAction => ({
    type: POST_SUBMIT_EXPENSE_REPORT_INIT,
  })
const createPostSubmitExpenseReportSuccessAction = (
  payload: PostSubmitExpenseReportSuccessPayload,
): PostSubmitExpenseReportSuccessAction => ({
  type: POST_SUBMIT_EXPENSE_REPORT_SUCCESS,
  payload,
})
const createPostSubmitExpenseReportFailureAction = (
  payload: PostSubmitExpenseReportErrorPayload,
): PostSubmitExpenseReportFailureAction => ({
  type: POST_SUBMIT_EXPENSE_REPORT_FAILURE,
  payload,
})

export const postSubmitExpenseReport = (
  expenseReportId: string,
  numberOfExpenses: number,
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostSubmitExpenseReportActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostSubmitExpenseReportActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostSubmitExpenseReportInitAction())
    return axios
      .post<PostSubmitExpenseReportSuccessPayload>(
        `/expensereports/${expenseReportId}/submit`,
        {},
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostSubmitExpenseReportSuccessAction(response.data))
          dispatch(createSetIsSubmitExpenseReportPopupOpen(false))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <SubmitExpenseReportToastMessage />,
            }),
          )
          analytics?.track(
            'out of pocket expense report details page - submit report - button press',
            {
              interaction: 'button press',
              intent: 'submit report',
              number_of_expenses: numberOfExpenses,
            },
          )
        },
        (err) => {
          dispatch(
            createPostSubmitExpenseReportFailureAction(err.response.data),
          )
        },
      )
  }
}

export const GET_SUBMITTER_USERS_INIT = 'data/users/GET_SUBMITTER_USERS_INIT'
export const GET_SUBMITTER_USERS_SUCCESS =
  'data/users/GET_SUBMITTER_USERS_SUCCESS'
export const GET_SUBMITTER_USERS_FAILURE =
  'data/users/GET_SUBMITTER_USERS_FAILURE'

export type GetSubmitterUsersSuccessPayload = UserListResponse[]

export type GetSubmitterUsersErrorPayload = BackendErrorPayload<string>

export interface GetSubmitterUsersInitAction {
  type: typeof GET_SUBMITTER_USERS_INIT
}
export interface GetSubmitterUsersSuccessAction {
  type: typeof GET_SUBMITTER_USERS_SUCCESS
  payload: GetSubmitterUsersSuccessPayload
}
export interface GetSubmitterUsersFailureAction {
  type: typeof GET_SUBMITTER_USERS_FAILURE
  payload: GetSubmitterUsersErrorPayload
}

export type GetSubmitterUsersActions =
  | GetSubmitterUsersInitAction
  | GetSubmitterUsersSuccessAction
  | GetSubmitterUsersFailureAction

const createGetSubmitterUsersInitAction = (): GetSubmitterUsersInitAction => ({
  type: GET_SUBMITTER_USERS_INIT,
})
const createGetSubmitterUsersSuccessAction = (
  payload: GetSubmitterUsersSuccessPayload,
): GetSubmitterUsersSuccessAction => ({
  type: GET_SUBMITTER_USERS_SUCCESS,
  payload,
})
const createGetSubmitterUsersFailureAction = (
  payload: GetSubmitterUsersErrorPayload,
): GetSubmitterUsersFailureAction => ({
  type: GET_SUBMITTER_USERS_FAILURE,
  payload,
})

export const getSubmitterUsers = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetSubmitterUsersActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetSubmitterUsersActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetSubmitterUsersInitAction())
    return axios
      .get<GetSubmitterUsersSuccessPayload>(
        `/expensereports/submitterusers`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetSubmitterUsersSuccessAction(response.data))
        },
        async (err) => {
          dispatch(createGetSubmitterUsersFailureAction(err.response.data))
        },
      )
  }
}

export const GET_EXPENSE_INIT = 'data/expenses/GET_EXPENSE_INIT'
export const GET_EXPENSE_SUCCESS = 'data/expenses/GET_EXPENSE_SUCCESS'
export const GET_EXPENSE_FAILURE = 'data/expenses/GET_EXPENSE_FAILURE'

export type GetExpenseSuccessPayload = ExpenseResponse

export type GetExpenseErrorPayload = BackendErrorPayload<string>
export interface GetExpenseInitAction {
  type: typeof GET_EXPENSE_INIT
}
export interface GetExpenseSuccessAction {
  type: typeof GET_EXPENSE_SUCCESS
  payload: GetExpenseSuccessPayload
}
export interface GetExpenseFailureAction {
  type: typeof GET_EXPENSE_FAILURE
  payload: GetExpenseErrorPayload
}

export type GetExpenseActions =
  | GetExpenseInitAction
  | GetExpenseSuccessAction
  | GetExpenseFailureAction

const createGetExpenseInitAction = (): GetExpenseInitAction => ({
  type: GET_EXPENSE_INIT,
})
const createGetExpenseSuccessAction = (
  payload: GetExpenseSuccessPayload,
): GetExpenseSuccessAction => ({
  type: GET_EXPENSE_SUCCESS,
  payload,
})
const createGetExpenseFailureAction = (
  payload: GetExpenseErrorPayload,
): GetExpenseFailureAction => ({
  type: GET_EXPENSE_FAILURE,
  payload,
})

export const getExpense = (
  id: string,
): ThunkAction<Promise<void>, RootState, void, GetExpenseActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetExpenseActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetExpenseInitAction())
    return axios
      .get<GetExpenseSuccessPayload>(
        `/expenses/${id}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetExpenseSuccessAction(response.data))
        },
        async (err) => {
          dispatch(createGetExpenseFailureAction(err.response.data))
        },
      )
  }
}

export const PATCH_EXPENSE_INIT = 'data/expenses/PATCH_EXPENSE_INIT'
export const PATCH_EXPENSE_SUCCESS = 'data/expenses/PATCH_EXPENSE_SUCCESS'
export const PATCH_EXPENSE_FAILURE = 'data/expenses/PATCH_EXPENSE_FAILURE'

export type PatchExpenseSuccessPayload = ExpenseResponse

export type PatchExpenseFailurePayload = BackendErrorPayload<string>

export interface PatchExpenseInitAction {
  type: typeof PATCH_EXPENSE_INIT
}
export interface PatchExpenseSuccessAction {
  type: typeof PATCH_EXPENSE_SUCCESS
  payload: PatchExpenseSuccessPayload
}
export interface PatchExpenseFailureAction {
  type: typeof PATCH_EXPENSE_FAILURE
  payload: PatchExpenseFailurePayload
}

export type PatchExpenseActions =
  | PatchExpenseInitAction
  | PatchExpenseSuccessAction
  | PatchExpenseFailureAction

const createPatchExpenseInitAction = (): PatchExpenseInitAction => ({
  type: PATCH_EXPENSE_INIT,
})
const createPatchExpenseSuccessAction = (
  payload: PatchExpenseSuccessPayload,
): PatchExpenseSuccessAction => ({
  type: PATCH_EXPENSE_SUCCESS,
  payload,
})
const createPatchExpenseFailureAction = (
  payload: PatchExpenseFailurePayload,
): PatchExpenseFailureAction => ({
  type: PATCH_EXPENSE_FAILURE,
  payload,
})

export const patchExpenseAndOpenToastAndCloseFlyout = (
  id: string | undefined,
  body: ExpenseFields,
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<Promise<void>, RootState, void, PatchExpenseActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PatchExpenseActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPatchExpenseInitAction())
    return axios
      .patch<PatchExpenseSuccessPayload>(
        `/expenses/${id}`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPatchExpenseSuccessAction(response.data))
          if (body.expenseReportId)
            dispatch(getExpenseReport(body.expenseReportId))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <UpdateExpenseToastMessage />,
            }),
          )
          analytics?.track(
            'update out of pocket expense report flyout - update expense- button press',
            {
              interaction: 'button press',
              intent: 'update expense',
              Expense_Name: body.expenseDate,
              Amount_Spent: toDollars(body.amountCents ?? 0),
              Expense_Date: body.expenseDate,
              Receipt_Uploaded: body.receiptAttachmentIds
                ? body.receiptAttachmentIds.length > 0
                : false,
            },
          )
        },
        (err) => {
          dispatch(createPatchExpenseFailureAction(err.response.data))
        },
      )
  }
}

export const DELETE_EXPENSE_INIT = 'data/expenses/DELETE_EXPENSE_INIT'
export const DELETE_EXPENSE_SUCCESS = 'data/expenses/DELETE_EXPENSE_SUCCESS'
export const DELETE_EXPENSE_FAILURE = 'data/expenses/DELETE_EXPENSE_FAILURE'

export type DeleteExpenseSuccessPayload = ExpenseReportResponse

export type DeleteExpenseFailurePayload = BackendErrorPayload<string>
export interface DeleteExpenseInitAction {
  type: typeof DELETE_EXPENSE_INIT
}
export interface DeleteExpenseSuccessAction {
  type: typeof DELETE_EXPENSE_SUCCESS
  payload: DeleteExpenseSuccessPayload
}
export interface DeleteExpenseFailureAction {
  type: typeof DELETE_EXPENSE_FAILURE
  payload: DeleteExpenseFailurePayload
}

export type DeleteExpenseActions =
  | DeleteExpenseInitAction
  | DeleteExpenseSuccessAction
  | DeleteExpenseFailureAction

const createDeleteExpenseInitAction = (): DeleteExpenseInitAction => ({
  type: DELETE_EXPENSE_INIT,
})
const createDeleteExpenseSuccessAction = (
  payload: DeleteExpenseSuccessPayload,
): DeleteExpenseSuccessAction => ({
  type: DELETE_EXPENSE_SUCCESS,
  payload,
})
const createDeleteExpenseFailureAction = (
  payload: DeleteExpenseFailurePayload,
): DeleteExpenseFailureAction => ({
  type: DELETE_EXPENSE_FAILURE,
  payload,
})

export const deleteExpense = (
  expenseIds: string[],
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<Promise<void>, RootState, void, DeleteExpenseActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, DeleteExpenseActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createDeleteExpenseInitAction())
    const authedJson = selectAuthedJsonApiRequestConfig(getState())
    return axios
      .delete(`/expenses`, {
        headers: authedJson.headers,
        baseURL: authedJson.baseURL,
        data: {
          expenses: expenseIds,
        },
      })
      .then(
        async (response) => {
          const location = selectLocationFromState(getState())
          const isBulkDelete =
            matchPath(location.pathname, expenseReportUrl(response.data.id)) !==
            null
          dispatch(createDeleteExpenseSuccessAction(response.data))
          dispatch(createSetIsDeleteExpensePopupOpen(false))
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <DeleteExpensesToastMessage expensesCount={expenseIds.length} />
              ),
            }),
          )
          analytics?.track(
            isBulkDelete
              ? 'out of pocket expense report details page - bulk delete - button press'
              : 'update out of pocket expense report flyout - delete expense - button press',
            {
              interaction: 'button press',
              intent: 'delete expense',
              Total_expenses_deleted: isBulkDelete
                ? expenseIds.length
                : undefined,
            },
          )
        },
        (err) => {
          dispatch(createDeleteExpenseFailureAction(err.response.data))
        },
      )
  }
}

export const POST_MARK_EXPENSE_REPORTS_PAID_INIT =
  'data/expenses/POST_MARK_EXPENSE_REPORTS_PAID_INIT'
export const POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS =
  'data/expenses/POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS'
export const POST_MARK_EXPENSE_REPORTS_PAID_FAILURE =
  'data/expenses/POST_MARK_EXPENSE_REPORTS_PAID_FAILURE'

export type PostMarkExpenseReportsPaidSuccessPayload = {
  expenseReports: ExpenseReportResponse[]
}

export type PostMarkExpenseReportsPaidErrorPayload = BackendErrorPayload<string>

export interface PostMarkExpenseReportsPaidInitAction {
  type: typeof POST_MARK_EXPENSE_REPORTS_PAID_INIT
}
export interface PostMarkExpenseReportsPaidSuccessAction {
  type: typeof POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS
  payload: PostMarkExpenseReportsPaidSuccessPayload
}
export interface PostMarkExpenseReportsPaidFailureAction {
  type: typeof POST_MARK_EXPENSE_REPORTS_PAID_FAILURE
  payload: PostMarkExpenseReportsPaidErrorPayload
}

export type PostMarkExpenseReportsPaidActions =
  | PostMarkExpenseReportsPaidInitAction
  | PostMarkExpenseReportsPaidSuccessAction
  | PostMarkExpenseReportsPaidFailureAction

const createPostMarkExpenseReportsPaidInitAction =
  (): PostMarkExpenseReportsPaidInitAction => ({
    type: POST_MARK_EXPENSE_REPORTS_PAID_INIT,
  })
const createPostMarkExpenseReportsPaidSuccessAction = (
  payload: PostMarkExpenseReportsPaidSuccessPayload,
): PostMarkExpenseReportsPaidSuccessAction => ({
  type: POST_MARK_EXPENSE_REPORTS_PAID_SUCCESS,
  payload,
})
const createPostMarkExpenseReportsPaidFailureAction = (
  payload: PostMarkExpenseReportsPaidErrorPayload,
): PostMarkExpenseReportsPaidFailureAction => ({
  type: POST_MARK_EXPENSE_REPORTS_PAID_FAILURE,
  payload,
})

export const postMarkExpenseReportsPaid = (
  expenseReportIds: string[],
  allReports?: boolean,
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PostMarkExpenseReportsPaidActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostMarkExpenseReportsPaidActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostMarkExpenseReportsPaidInitAction())
    return axios
      .post<PostMarkExpenseReportsPaidSuccessPayload>(
        `/expensereports/markaspaid`,
        {
          expenseReports: expenseReportIds,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostMarkExpenseReportsPaidSuccessAction(response.data))
          dispatch(createSetIsMarkExpenseReportsPaidPopupOpen(false))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <MarkExpenseReportsAsPaidToastMessage
                  expenseReportsCount={expenseReportIds.length}
                  allReports={allReports}
                />
              ),
            }),
          )
          analytics?.track(
            allReports
              ? 'Expense Report Landing Page - bulk Mark as paid - button press'
              : 'Expense Report Details Page - Mark as paid - button press',
            {
              interaction: 'button press',
              intent: 'mark as paid',
              Total_reports_paid: allReports
                ? expenseReportIds.length
                : undefined,
            },
          )
        },
        (err) => {
          dispatch(
            createPostMarkExpenseReportsPaidFailureAction(err.response.data),
          )
        },
      )
  }
}

export const DELETE_EXPENSE_REPORTS_INIT =
  'data/expenses/DELETE_EXPENSE_REPORTS_INIT'
export const DELETE_EXPENSE_REPORTS_SUCCESS =
  'data/expenses/DELETE_EXPENSE_REPORTS_SUCCESS'
export const DELETE_EXPENSE_REPORTS_FAILURE =
  'data/expenses/DELETE_EXPENSE_REPORTS_FAILURE'

export type DeleteExpenseReportsSuccessPayload = {
  expenseReports: {
    deleted: string[]
  }
}

export type DeleteExpenseReportsFailurePayload = BackendErrorPayload<string>
export interface DeleteExpenseReportsInitAction {
  type: typeof DELETE_EXPENSE_REPORTS_INIT
}
export interface DeleteExpenseReportsSuccessAction {
  type: typeof DELETE_EXPENSE_REPORTS_SUCCESS
  payload: DeleteExpenseReportsSuccessPayload
}
export interface DeleteExpenseReportsFailureAction {
  type: typeof DELETE_EXPENSE_REPORTS_FAILURE
  payload: DeleteExpenseReportsFailurePayload
}

export type DeleteExpenseReportsActions =
  | DeleteExpenseReportsInitAction
  | DeleteExpenseReportsSuccessAction
  | DeleteExpenseReportsFailureAction

const createDeleteExpenseReportsInitAction =
  (): DeleteExpenseReportsInitAction => ({
    type: DELETE_EXPENSE_REPORTS_INIT,
  })
const createDeleteExpenseReportsSuccessAction = (
  payload: DeleteExpenseReportsSuccessPayload,
): DeleteExpenseReportsSuccessAction => ({
  type: DELETE_EXPENSE_REPORTS_SUCCESS,
  payload,
})
const createDeleteExpenseReportsFailureAction = (
  payload: DeleteExpenseReportsFailurePayload,
): DeleteExpenseReportsFailureAction => ({
  type: DELETE_EXPENSE_REPORTS_FAILURE,
  payload,
})

export const deleteExpenseReports = (
  expenseReportsIds: string[],
  analytics?: ExtendAnalyticsBrowser,
): ThunkAction<Promise<void>, RootState, void, DeleteExpenseReportsActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, DeleteExpenseReportsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createDeleteExpenseReportsInitAction())
    const authedJson = selectAuthedJsonApiRequestConfig(getState())
    return axios
      .delete(`/expensereports/deleteall`, {
        headers: authedJson.headers,
        baseURL: authedJson.baseURL,
        data: {
          expenseReports: expenseReportsIds,
        },
      })
      .then(
        async (response) => {
          dispatch(createDeleteExpenseReportsSuccessAction(response.data))
          dispatch(createSetIsDeleteExpenseReportsPopupOpen(false))
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <DeleteExpenseReportsToastMessage
                  expenseReportsCount={expenseReportsIds.length}
                />
              ),
            }),
          )
          dispatch(getSubmitterExpenseReportsLength())
          analytics?.track(
            'Expense Report Landing Page - bulk delete - button press',
            {
              interaction: 'button press',
              intent: 'bulk delete reports',
              Total_reports_deleted: expenseReportsIds.length,
            },
          )
        },
        (err) => {
          dispatch(createDeleteExpenseReportsFailureAction(err.response.data))
        },
      )
  }
}

export const SET_EMPTY_STATE_EXPENSE_REPORTS =
  'app/expenseReportsFullPaginatedData/SET_EMPTY_STATE_EXPENSE_REPORTS'
export interface SetEmptyStateExpenseReportsAction {
  type: typeof SET_EMPTY_STATE_EXPENSE_REPORTS
}

export const RESET_BULK_SELECT = 'app/expensereport/RESET_BULK_SELECT'

export interface ResetBulkSelectAction {
  type: typeof RESET_BULK_SELECT
}

export const PATCH_UPDATE_EXPENSE_REPORT_INIT =
  'data/expenses/PATCH_UPDATE_EXPENSE_REPORT_INIT'
export const PATCH_UPDATE_EXPENSE_REPORT_SUCCESS =
  'data/expenses/PATCH_UPDATE_EXPENSE_REPORT_SUCCESS'
export const PATCH_UPDATE_EXPENSE_REPORT_FAILURE =
  'data/expenses/PATCH_UPDATE_EXPENSE_REPORT_FAILURE'

export type PatchUpdateExpenseReportSuccessPayload = ExpenseReportResponse

export type PatchUpdateExpenseReportErrorPayload = BackendErrorPayload<string>

export interface PatchUpdateExpenseReportInitAction {
  type: typeof PATCH_UPDATE_EXPENSE_REPORT_INIT
}
export interface PatchUpdateExpenseReportSuccessAction {
  type: typeof PATCH_UPDATE_EXPENSE_REPORT_SUCCESS
  payload: PatchUpdateExpenseReportSuccessPayload
}
export interface PatchUpdateExpenseReportFailureAction {
  type: typeof PATCH_UPDATE_EXPENSE_REPORT_FAILURE
  payload: PatchUpdateExpenseReportErrorPayload
}

export type PatchUpdateExpenseReportActions =
  | PatchUpdateExpenseReportInitAction
  | PatchUpdateExpenseReportSuccessAction
  | PatchUpdateExpenseReportFailureAction

const createPatchUpdateExpenseReportInitAction =
  (): PatchUpdateExpenseReportInitAction => ({
    type: PATCH_UPDATE_EXPENSE_REPORT_INIT,
  })
const createPatchUpdateExpenseReportSuccessAction = (
  payload: PatchUpdateExpenseReportSuccessPayload,
): PatchUpdateExpenseReportSuccessAction => ({
  type: PATCH_UPDATE_EXPENSE_REPORT_SUCCESS,
  payload,
})
const createPatchUpdateExpenseReportFailureAction = (
  payload: PatchUpdateExpenseReportErrorPayload,
): PatchUpdateExpenseReportFailureAction => ({
  type: PATCH_UPDATE_EXPENSE_REPORT_FAILURE,
  payload,
})

export const patchUpdateExpenseReport = (
  reportName: string,
  expenseReportId: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PatchUpdateExpenseReportActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PatchUpdateExpenseReportActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPatchUpdateExpenseReportInitAction())
    return axios
      .patch<PatchUpdateExpenseReportSuccessPayload>(
        `/expensereports/${expenseReportId}`,
        {
          name: reportName,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPatchUpdateExpenseReportSuccessAction(response.data))
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <UpdateExpenseReportNameSuccessToastMessage />,
            }),
          )
        },
        (err) => {
          dispatch(
            createPatchUpdateExpenseReportFailureAction(err.response.data),
          )
        },
      )
  }
}
